import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AuthSession, currentSession } from './api/auth';
import { SIGNED_IN_ACCOUNT_KEY } from './lib/signedInAccount';

interface SessionState {
  session: AuthSession | null | undefined;
  error: string;
  refresh: () => Promise<void>;
  setSession: (session: AuthSession | null) => void;
}

/** Exported so route tests can render a signed-in tree without a network round trip. */
export const SessionContext = createContext<SessionState | null>(null);

function sameSession(a: AuthSession, b: AuthSession): boolean {
  return a.org_id === b.org_id && a.principal_id === b.principal_id
    && (a.session_id ?? '') === (b.session_id ?? '') && Boolean(a.owner_org) === Boolean(b.owner_org);
}

/** Re-check the session on another tab's sign-in/out and on focus. Returns the unsubscribe. */
export function watchSessionChanges(target: EventTarget, recheck: () => void): () => void {
  const onStorage = (event: Event) => {
    const key = (event as StorageEvent).key;
    if (key === null || key === SIGNED_IN_ACCOUNT_KEY) recheck();
  };
  const onFocus = () => recheck();
  target.addEventListener('storage', onStorage);
  target.addEventListener('focus', onFocus);
  return () => {
    target.removeEventListener('storage', onStorage);
    target.removeEventListener('focus', onFocus);
  };
}

/**
 * Orders session reads. Each read takes a ticket; only the newest outstanding ticket may apply its
 * answer, and an explicit sign-in or sign-out invalidates every read still in flight. Without it an
 * older answer arriving late could put back an account the person just left.
 */
export function createSessionSequencer() {
  let latest = 0;
  return {
    begin: (): number => ++latest,
    isCurrent: (ticket: number): boolean => ticket === latest,
    invalidate: (): void => { latest += 1; },
  };
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSessionState] = useState<AuthSession | null | undefined>(undefined);
  const [error, setError] = useState('');
  const sequencer = useRef(createSessionSequencer()).current;
  const latestSession = useRef(session);
  latestSession.current = session;

  /** An explicit change (sign-in, sign-out) wins over any read still in flight. */
  const setSession = useCallback((next: AuthSession | null) => {
    sequencer.invalidate();
    setSessionState(next);
  }, [sequencer]);

  const refresh = useCallback(async () => {
    const ticket = sequencer.begin();
    setError('');
    try {
      const next = await currentSession();
      if (sequencer.isCurrent(ticket)) setSessionState(next);
    } catch (failure) {
      if (!sequencer.isCurrent(ticket)) return;
      setSessionState(null);
      setError(failure instanceof Error ? failure.message : 'session state could not be loaded');
    }
  }, [sequencer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Background re-check: a different account (or none) replaces the session; the same session keeps
   * its object so nothing re-renders; a failed check changes nothing (the next request will tell).
   */
  const recheck = useCallback(async () => {
    const ticket = sequencer.begin();
    let next: AuthSession | null;
    try {
      next = await currentSession();
    } catch (failure) {
      // An established session survives a failed background check. But if this check overtook the
      // first load, it must end the loading state the way that load would have.
      if (sequencer.isCurrent(ticket) && latestSession.current === undefined) {
        setSessionState(null);
        setError(failure instanceof Error ? failure.message : 'session state could not be loaded');
      }
      return;
    }
    if (!sequencer.isCurrent(ticket)) return;
    if (next) setError('');
    setSessionState((previous) => (previous && next && sameSession(previous, next) ? previous : next));
  }, [sequencer]);

  // The session cookie is shared by every tab. When another tab signs in or out (it rewrites the
  // remembered account), or this tab regains focus, re-read who this browser is, so the top bar
  // never keeps naming an account the cookie no longer belongs to (walkthrough F-38).
  useEffect(() => (typeof window === 'undefined' ? undefined : watchSessionChanges(window, recheck)), [recheck]);

  const value = useMemo(() => ({ session, error, refresh, setSession }), [session, error, refresh, setSession]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
